import React, { useState, useRef, useEffect } from 'react';
import { Collapse, Input, Button, Typography, Card, List, Tag, Space, message as antMessage, Spin } from 'antd';
import { Sparkles, User, Upload as UploadIcon, Send, Paperclip } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import FileUploadModal from './FileUploadModal';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

const ChatComponent = ({
  data,
  title = "Agent Conversation",
  uuid,
  currentVersion = 1,
  versionInfo = {},
  onVersionChange,
  onSendMessage,
  onFileUpload,
  onComponentClick,
  componentRefs = {},
  isLoading = false,
  sessionId,
  agent_key,
  project_id,
  component_map
}) => {
  // State to track whether the component is expanded
  const [isExpanded, setIsExpanded] = useState(true);
  
  // Store the expand icon state update action for later execution
  const expandIconRef = useRef(null);
  
  // State for the new message input
  const [newMessage, setNewMessage] = useState('');
  
  // State for upload functionality
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [currentUploadRequest, setCurrentUploadRequest] = useState(null);
  const [uploadProcessing, setUploadProcessing] = useState(false);
  
  // State to track sending message at the component level
  const [sendingMessage, setSendingMessage] = useState(false);

  // State to prevent re-renders during layout transition
  const [isStable, setIsStable] = useState(false);
  
  // Reference to the chat container for scrolling
  const messagesEndRef = useRef(null);
  
  // Ref to store the message being sent in case we need to restore it
  const messageToSendRef = useRef('');
  
  // Safely update expanded state after render is complete
  useEffect(() => {
    if (expandIconRef.current !== null) {
      setIsExpanded(expandIconRef.current);
      expandIconRef.current = null;
    }
  }, []);
  
  // Set stable after initial render
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsStable(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);
  
  // Handler for version navigation
  const handleVersionChange = (newVersion) => {
    if (onVersionChange && uuid) {
      onVersionChange(uuid, newVersion);
    }
  };
  
  // Handle file upload button click
  const handleFileUploadClick = (uploadRequest) => {
    setCurrentUploadRequest(uploadRequest);
    setUploadModalVisible(true);
  };
  
  // Handle file upload modal close
  const handleUploadModalClose = (success) => {
    setUploadModalVisible(false);
    
    // If upload was cancelled, send a message to the chat
    if (success === false && currentUploadRequest) {
      // You can optionally send a message to the backend about the cancellation
      onSendMessage(`I've cancelled the upload for ${currentUploadRequest.title || 'files'}.`, uuid, null, agent_key);
    }
    
    setCurrentUploadRequest(null);
  };
  
  // Handle actual file upload
  const handleFileUpload = async (files, context, sessionId) => {
    try {
      setUploadProcessing(true);
      
      if (!onFileUpload) {
        throw new Error('File upload handler not provided');
      }
      
      const success = await onFileUpload(files, uuid, context, sessionId);
      return success;
    } catch (error) {
      console.error('Error uploading files:', error);
      return false;
    } finally {
      setUploadProcessing(false);
    }
  };
  
  // Handler for sending a new message
  const handleSendMessage = async () => {
    if (newMessage.trim() && onSendMessage && !sendingMessage) {
      try {
        // Set sending state
        setSendingMessage(true);
        
        // Store message for sending in the ref
        messageToSendRef.current = newMessage;
        
        // Clear the input field immediately for better UX
        setNewMessage('');
        
        // Call the onSendMessage prop (which will be defined in DraggableLayoutManager)
        const success = await onSendMessage(messageToSendRef.current, uuid, null, agent_key, project_id);
        
        // Check success status
        if (!success) {
          // Use antMessage from antd which is properly imported
          antMessage.error('Failed to send message');
          // Restore the message to the input field
          setNewMessage(messageToSendRef.current);
        }
      } catch (error) {
        console.error('Error sending message:', error);
        antMessage.error(`Error: ${error.message}`);
        
        // On error, try to restore the message to the input field
        if (newMessage.trim() === '') {
          setNewMessage(messageToSendRef.current || '');
        }
      } finally {
        setSendingMessage(false);
      }
    }
  };
  
  // Handle enter key press to send message
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  
  // Fixed handleComponentClick function for ChatComponent.jsx
  const handleComponentClick = (componentRef) => {
    if (!componentRef) return;
    
    // Prevent rapid clicking or clicks during message sending
    if (sendingMessage) return;
    
    console.log("Component reference clicked:", componentRef);
    
    try {
      // Check if this is a file upload request
      if (componentRef.type === 'fileUploadRequest') {

        // Add component_map to the upload request
        const uploadRequest = {
          ...componentRef
          //component_map: componentRefs?.component_map
        };

        handleFileUploadClick(uploadRequest);
        return;
      }
      
      // First, try to use the onComponentClick prop if it exists
      if (onComponentClick) {
        onComponentClick(componentRef);
        return;
      }
      
      // Otherwise try to use onVersionChange as a fallback
      if (onVersionChange && componentRef.uuid) {
        // This will update the component to the specific version
        const version = componentRef.version || 1;
        onVersionChange(componentRef.uuid, version);
        
        // Show a success message
        antMessage.info(`Loading ${componentRef.title || 'component'}...`);
      } else {
        // If we can't navigate properly, show a message
        antMessage.warning(`Couldn't navigate to ${componentRef.title || 'component'}`);
      }
    } catch (error) {
      console.error('Error navigating to component:', error);
      antMessage.error('Error navigating to component');
    }
  };

  // Function to render component reference
  const renderComponentReference = (componentRef) => {
    const { componentKey, componentType, title, type } = componentRef;
    
    // Special handling for file upload buttons
    if (type === 'fileUploadRequest') {
      return (
        <button 
          style={{ 
            margin: '4px',
            backgroundColor: '#e6f7ff',
            border: 'none',
            borderLeft: '3px solid #1890ff',
            borderRadius: '4px',
            padding: '8px 16px',
            fontWeight: 'normal',
            color: '#000',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center'
          }}
          onClick={() => handleComponentClick(componentRef)}
        >
          <Paperclip size={16} style={{ marginRight: '8px' }} />
          {title || 'Upload Files'}
        </button>
      );
    }
    
    // Generate button style based on component type
    let buttonStyle = { 
      margin: '4px',
      border: 'none',
      borderRadius: '4px',
      padding: '8px 16px',
      fontWeight: 'normal',
      color: '#000',
      cursor: 'pointer',
      transition: 'all 0.3s ease' // Smooth transition for hover effects
    };
    
    // Customize button style based on component type
    if (componentType === 'list_component') {
      buttonStyle.backgroundColor = '#e0f7fa'; // Light blue for lists
      buttonStyle.borderLeft = '3px solid #00b0ff';
    } else if (componentType === 'search_results') {
      buttonStyle.backgroundColor = '#f1f8e9'; // Light green for search
      buttonStyle.borderLeft = '3px solid #7cb342';
    } else {
      buttonStyle.backgroundColor = '#e0e0e0'; // Default gray
      buttonStyle.borderLeft = '3px solid #9e9e9e';
    }
    
    return (
      <button 
        style={buttonStyle}
        onClick={() => handleComponentClick(componentRef)}
      >
        {title || componentKey}
      </button>
    );
  };
  
  // Function to render file reference
  const renderFileReference = (fileRef) => {
    const { fileName, fileSize, fileType } = fileRef;
    
    return (
      <button
        style={{ 
          margin: '4px',
          backgroundColor: '#fce4ec', // Light pink for files
          border: 'none',
          borderLeft: '3px solid #ec407a',
          borderRadius: '4px',
          padding: '8px 16px',
          fontWeight: 'normal',
          color: '#000',
          cursor: 'pointer',
          transition: 'all 0.3s ease'
        }}
      >
        {fileName || "Uploaded File"}
      </button>
    );
  };
  
  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current && isStable) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [data?.messages, isStable]);
  
  // Check if version navigation should be displayed
  const showVersionNavigation = versionInfo && 
                               versionInfo.available_versions && 
                               versionInfo.available_versions.length > 1;
  
  // If data is not yet available, show a placeholder
  if (!data || !data.messages) {
    return (
      <div style={{ 
        marginBottom: '16px', 
        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
        borderRadius: '4px',
        border: '2px solid #6200EE',
        padding: '20px',
        textAlign: 'center',
        backgroundColor: '#f5f5f5'
      }}>
        <Spin size="large" />
        <div style={{ marginTop: '10px' }}>Loading conversation...</div>
      </div>
    );
  }
  
  // Function to handle expand icon click - store in ref rather than updating state directly
  const handleExpandIconClick = (isActive) => {
    expandIconRef.current = isActive;
    return isActive ? (
      <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none">
        <polyline points="6 9 12 15 18 9"></polyline>
      </svg>
    ) : (
      <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none">
        <polyline points="9 18 15 12 9 6"></polyline>
      </svg>
    );
  };
  
  // Custom styles for markdown content
  const markdownStyles = {
    p: { margin: '0 0 10px 0' },
    h1: { fontSize: '24px', marginTop: '20px', marginBottom: '10px' },
    h2: { fontSize: '20px', marginTop: '16px', marginBottom: '8px' },
    h3: { fontSize: '18px', marginTop: '14px', marginBottom: '7px' },
    strong: { fontWeight: 'bold' },
    em: { fontStyle: 'italic' },
    ul: { marginLeft: '5px', marginBottom: '10px' },
    ol: { marginLeft: '5px', marginBottom: '10px' },
    li: { margin: '2px 0' },
    code: {
      backgroundColor: '#f5f5f5',
      padding: '2px 4px',
      borderRadius: '3px',
      fontFamily: 'monospace'
    },
    pre: {
      backgroundColor: '#f5f5f5',
      padding: '10px',
      borderRadius: '5px',
      overflow: 'auto',
      marginBottom: '10px'
    }
  };
  
  return (
    <div style={{ 
      marginBottom: '16px', 
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
      borderRadius: '4px',
      border: '0px solid #6200EE',
      maxHeight: 'calc(100vh - 150px)' ,
      position: 'relative'
    }}>
      {/* Display a localized loading overlay when isLoading is true */}
      {isLoading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 100,
          borderRadius: '4px',
        }}>
          <Spin size="large" />
          <div style={{ marginTop: '15px', fontSize: '16px', color: '#6200EE' }}>
            Generating response...
          </div>
        </div>
      )}
      
      {/* File Upload Modal */}
      <FileUploadModal
        visible={uploadModalVisible}
        onCancel={handleUploadModalClose}
        onUpload={handleFileUpload}
        title={currentUploadRequest?.title || "Upload Files"}
        maxFiles={currentUploadRequest?.maxFiles || 5}
        acceptedFileTypes={currentUploadRequest?.acceptedFileTypes || "*"}
        sessionId={sessionId}
        requestContext={currentUploadRequest}
      />
      
      <Collapse
        defaultActiveKey={['1']}
        expandIcon={({ isActive }) => handleExpandIconClick(isActive)}
        items={[
          {
            key: '1',
            label: (
              <div style={{ 
                fontWeight: 'bold',
                fontSize: '18px',
                padding: '4px 0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <span>{title}</span>
                {/* Small loading indicator in the header when processing */}
                {isLoading && (
                  <Spin size="small" style={{ marginLeft: '10px' }} />
                )}
              </div>
            ),
            children: (
              <div style={{ background: '#e5e5e5', padding: '0', borderRadius: '0 0 4px 4px' }}>
                {/* Chat messages */}
                <div 
                  style={{ 
                    height: '55vh', 
                    overflowY: 'auto',
                    padding: '8px',
                    background: '#e5e5e5',
                    borderRadius: '0',
                  }}
                >
                  <List
                    itemLayout="horizontal"
                    dataSource={data?.messages || []}
                    renderItem={(message, index) => (
                      <List.Item
                        key={index}
                        style={{ 
                          padding: '4px 0',
                          border: 'none',
                          display: 'flex',
                          justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start'
                        }}
                      >
                        <Card
                          style={{ 
                            maxWidth: '95%',
                            backgroundColor: 'white',
                            borderColor: 'transparent',
                            borderRadius: '12px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            margin: '0'
                          }}
                          bodyStyle={{ 
                            padding: '12px 16px',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                            {message.role !== 'user' && (
                              <div
                                style={{
                                  backgroundColor: '#f0e6ff',
                                  borderRadius: '50%',
                                  width: '40px',
                                  height: '40px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  marginRight: '12px',
                                  flexShrink: 0
                                }}
                              >
                                <Sparkles size={24} color="#6200EE" />
                              </div>
                            )}
                            <div style={{ flexGrow: 1 }}>
                              {/* Use ReactMarkdown component to render content with markdown */}
                              <div className="markdown-content">
                                <ReactMarkdown
                                  components={{
                                    p: ({ node, ...props }) => <p style={markdownStyles.p} {...props} />,
                                    h1: ({ node, ...props }) => <h1 style={markdownStyles.h1} {...props} />,
                                    h2: ({ node, ...props }) => <h2 style={markdownStyles.h2} {...props} />,
                                    h3: ({ node, ...props }) => <h3 style={markdownStyles.h3} {...props} />,
                                    strong: ({ node, ...props }) => <strong style={markdownStyles.strong} {...props} />,
                                    em: ({ node, ...props }) => <em style={markdownStyles.em} {...props} />,
                                    ul: ({ node, ...props }) => <ul style={markdownStyles.ul} {...props} />,
                                    ol: ({ node, ...props }) => <ol style={markdownStyles.ol} {...props} />,
                                    li: ({ node, ...props }) => <li style={markdownStyles.li} {...props} />,
                                    code: ({ node, inline, ...props }) => 
                                      inline ? 
                                        <code style={markdownStyles.code} {...props} /> : 
                                        <code style={markdownStyles.pre} {...props} />,
                                    pre: ({ node, ...props }) => <pre style={markdownStyles.pre} {...props} />
                                  }}
                                >
                                  {message.content}
                                </ReactMarkdown>
                              </div>
                              
                              {/* Render component references if present */}
                              {message.componentRefs && message.componentRefs.length > 0 && (
                                <div style={{ marginTop: '12px' }}>
                                  <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                                    {message.componentRefs.map((ref, i) => (
                                      <span key={`comp-ref-${i}`}>
                                        {renderComponentReference(ref)}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {/* Render file references if present */}
                              {message.fileRefs && message.fileRefs.length > 0 && (
                                <div style={{ marginTop: '12px' }}>
                                  <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                                    {message.fileRefs.map((ref, i) => (
                                      <span key={`file-ref-${i}`}>
                                        {renderFileReference(ref)}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Render file upload requests if present */}
                              {message.fileUploadRequests && message.fileUploadRequests.length > 0 && (
                                <div style={{ marginTop: '12px' }}>
                                  <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                                    {message.fileUploadRequests.map((request, i) => (
                                      <span key={`file-upload-${i}`}>
                                        {renderComponentReference({
                                          ...request,
                                          type: 'fileUploadRequest'
                                        })}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                            {message.role === 'user' && (
                              <div
                                style={{
                                  backgroundColor: '#6200EE',
                                  borderRadius: '50%',
                                  width: '40px',
                                  height: '40px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  marginLeft: '12px',
                                  flexShrink: 0
                                }}
                              >
                                <User size={24} color="white" />
                              </div>
                            )}
                          </div>
                        </Card>
                      </List.Item>
                    )}
                  />
                  <div ref={messagesEndRef} />
                  
                  {/* Show loading indicator when sending message (separate from isLoading) */}
                  {sendingMessage && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(255, 255, 255, 0.4)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 10,
                      borderRadius: '4px',
                      pointerEvents: 'none' // Allow clicks to pass through
                    }}>
                      <div style={{ 
                        backgroundColor: 'rgba(180, 164, 203, 0.8)',
                        padding: '6px 12px',
                        borderRadius: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}>
                        <Spin size="small" style={{ marginRight: '8px' }} />
                        <span style={{ color: '#6200EE', fontWeight: 'bold' }}>Sending...</span>
                      </div>
                    </div>
                  )}

                </div>
                
                {/* Message input */}
                <div style={{ 
                  display: 'flex', 
                  padding: '16px',
                  background: '#e5e5e5',
                  position: 'relative' 
                }}>
                  <TextArea
                    value={newMessage}
                    onChange={e => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Type a message..."
                    autoSize={{ minRows: 2, maxRows: 4 }}
                    style={{ 
                      borderRadius: '24px',
                      marginRight: '12px',
                      padding: '12px 16px',
                      fontSize: '16px',
                      backgroundColor: 'white',
                      border: '1px solid #d9d9d9',
                      height: '50px'
                    }}
                    disabled={sendingMessage || isLoading || uploadProcessing}
                  />
                  <Button 
                    type="primary"
                    onClick={handleSendMessage}
                    style={{ 
                      borderRadius: '16px',
                      width: '50px',
                      height: '75px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#6200EE',
                      border: 'none'
                    }}
                    loading={sendingMessage || isLoading || uploadProcessing}
                    disabled={!newMessage.trim() || sendingMessage || isLoading || uploadProcessing}
                  >
                    {!sendingMessage && !isLoading && !uploadProcessing && <Send size={24} color="white" />}
                  </Button>
                </div>
              </div>
            )
          }
        ]}
      />
    </div>
  );
};

export default ChatComponent;